/**
 * This component is used to wrap a multi step dialog. Use thise when you have a dialog that has multiple steps.
 * Children of this component should be DialogStep components.
 */
import { useIframeState } from '../Iframe/IframeStateContext';
import { BaseProps } from '../types';
import { useIsMobile } from '#hooks';
import { Dialog as MUIDialog, DialogProps as MUIDialogProps } from '@mui/material';
import React, { FC } from 'react';

type Props = BaseProps & {
  /**
   * Whether the dialog is open or not
   */
  open: boolean;

  /**
   * Callback when the dialog is closed from the top right X icon
   */
  onClose?: () => void;

  /**
   * The content of the dialog
   * Can be a string or a JSX element
   * If it's a string, it will be rendered as a Label
   */
  children: React.ReactNode | React.ReactNode[];

  maxWidth?: MUIDialogProps['maxWidth'];
  enableEscapeClose?: boolean;
  enableBackdropClickClose?: boolean;
};

type DialogCloseEvent = Parameters<NonNullable<MUIDialogProps['onClose']>>[0];

const MultiStepDialog: FC<Props> = ({ children, ...props }) => {
  const { enableEscapeClose, enableBackdropClickClose, onClose, maxWidth, dataTestId, open } = props;
  const isMobile = useIsMobile();
  const { iframeWindow } = useIframeState();
  const container = iframeWindow?.document.body;
  /**
   * By default, only the cross icon can close the modal.
   */
  const handleClose = (_e: DialogCloseEvent, reason: string) => {
    if (!enableEscapeClose && reason === 'escapeKeyDown') {
      return;
    }
    if (!enableBackdropClickClose && reason === 'backdropClick') {
      return;
    }
    onClose?.();
  };

  // MUI 5.18 types don't properly expose all Dialog props through intersection types
  const dialogProps = {
    container,
    'data-testid': dataTestId,
    fullScreen: isMobile,
    disableScrollLock: true,
    scroll: 'paper' as const,
    open,
    maxWidth,
    onClose: handleClose,
  };

  // SAFETY: dialogProps contains only MUI Dialog fields, including the required open and onClose values.
  return <MUIDialog {...(dialogProps as MUIDialogProps)}>{children}</MUIDialog>;
};

export default MultiStepDialog;
